import React, { ReactNode, FC } from 'react'
import clsx from 'clsx'
import { observer } from 'mobx-react'

export enum LFButtonType {
  PRIMARY = 'primary',
  OUTLINE = 'outline',
  PLAIN = 'plain',
}
export enum LFButtonColor {
  MAIN = 'main',
  SUCCESS = 'success',
  WARNING = 'warning',
  NEUTRAL = 'neutral',
  DANGER = 'danger',
  INFORMATION = 'information',
  GREY = 'grey',
}

export interface LFButtonProps {
  type: LFButtonType
  className?: string
  disabled?: boolean
  color?: LFButtonColor
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  buttonType?: 'submit' | 'button' | 'reset'
  padding?: string
  borderRadius?: string
  gap?: string
  testId?: string
  children: ReactNode
  full?: boolean
  justifyContent?: string
  tooltipId?: string
  tooltipContent?: string
}

export const LFButton: FC<LFButtonProps> = observer(
  ({
    type,
    children,
    className,
    onClick,
    full = false,
    disabled,
    gap = 'gap-x-4',
    color,
    buttonType,
    padding = 'px-16 py-4',
    borderRadius = 'rounded-lg',
    testId,
    justifyContent = 'justify-center',
    tooltipId = 'main-tooltip',
    tooltipContent,
  }) => {
    const bgColor = clsx({
      'bg-primary-300': type === 'primary' && color === 'main' && disabled,
      'border border-primary-300 bg-transparent':
        type === 'outline' && color === 'main' && disabled,
      'bg-primary-300 hover:bg-primary-200 active:bg-primary-400':
        type === 'primary' && color === 'main' && !disabled,
      'border border-primary-300 bg-transparent hover:bg-primary-50 active:bg-primary-100':
        type === 'outline' && color === 'main' && !disabled,

      'bg-positive-300': type === 'primary' && color === 'success' && disabled,
      'border border-positive-300 bg-transparent':
        type === 'outline' && color === 'success' && disabled,
      'bg-positive-300 hover:bg-positive-200 active:bg-positive-400':
        type === 'primary' && color === 'success' && !disabled,
      'border border-positive-300 bg-transparent hover:bg-positive-50 active:bg-positive-100':
        type === 'outline' && color === 'success' && !disabled,

      'bg-warning-500': type === 'primary' && color === 'warning' && disabled,
      'border border-warning-500 bg-transparent':
        type === 'outline' && color === 'warning' && disabled,
      'bg-warning-500 hover:bg-warning-200 active:bg-warning-400':
        type === 'primary' && color === 'warning' && !disabled,
      'border border-warning-500 bg-transparent hover:bg-warning-50 active:bg-warning-100':
        type === 'outline' && color === 'warning' && !disabled,

      'bg-spindle': type === 'primary' && color === 'neutral' && disabled,
      'border border-spindle bg-transparent':
        type === 'outline' && color === 'neutral' && disabled,
      'bg-spindle hover:bg-spindle-dark active:bg-spindle':
        type === 'primary' && color === 'neutral' && !disabled,

      'bg-negative-300': type === 'primary' && color === 'danger' && disabled,
      'border border-negative-300 bg-transparent':
        type === 'outline' && color === 'danger' && disabled,
      'bg-negative-300 hover:bg-negative-200 active:bg-negative-400':
        type === 'primary' && color === 'danger' && !disabled,
      'border border-negative-300 bg-transparent hover:bg-negative-50 active:bg-negative-100':
        type === 'outline' && color === 'danger' && !disabled,

      'border border-transparent bg-transparent hover:border-negative-300':
        type === 'plain' && color === 'danger' && !disabled,

      'border border-transparent bg-transparent hover:border-warning-500':
        type === 'plain' && color === 'warning' && !disabled,

      'border border-transparent bg-transparent hover:border-secondary-300':
        type === 'plain' && color === 'information' && !disabled,

      'border border-transparent bg-transparent hover:border-grey':
        type === 'plain' && color === 'grey' && !disabled,

      'border border-transparent bg-transparent hover:border-primary-300':
        type === 'plain' && color === 'main' && !disabled,

      'border border-transparent bg-transparent hover:border-positive-300':
        type === 'plain' && color === 'success' && !disabled,

      'bg-secondary-300':
        type === 'primary' && color === 'information' && disabled,
      'border border-secondary-300 bg-transparent':
        type === 'outline' && color === 'information' && disabled,
      'bg-secondary-300 hover:bg-secondary-200 active:bg-secondary-400':
        type === 'primary' && color === 'information' && !disabled,
      'border border-secondary-300 bg-transparent hover:bg-secondary-50 active:bg-secondary-100':
        type === 'outline' && color === 'information' && !disabled,

      'bg-grey': type === 'primary' && color === 'grey' && disabled,
      'border border-grey bg-transparent':
        type === 'outline' && color === 'grey' && disabled,
      'hover:bg-grey-200 active:bg-grey-400 bg-grey':
        type === 'primary' && color === 'grey' && !disabled,
      'hover:bg-grey-50 active:bg-grey-100 border border-grey bg-transparent':
        type === 'outline' && color === 'grey' && !disabled,

      'bg-grey hover:bg-silver active:bg-black':
        type === 'primary' && color === 'grey' && !disabled,
    })

    const textColor = clsx({
      'text-white': type === 'primary',
      'text-primary-300':
        (type === 'outline' || type === 'plain') && color === 'main',
      'text-positive-300':
        (type === 'outline' || type === 'plain') && color === 'success',
      'text-warning-500':
        (type === 'outline' || type === 'plain') && color === 'warning',
      'text-negative-300':
        (type === 'outline' || type === 'plain') && color === 'danger',
      'text-secondary-300':
        (type === 'outline' || type === 'plain') && color === 'information',
      'text-grey': (type === 'outline' || type === 'plain') && color === 'grey',
    })

    const shadow = clsx({
      'shadow-xs': disabled && type !== 'plain',
      'shadow-xs hover:shadow-md': !disabled && type !== 'plain' && !full,
      'shadow-none': type === 'plain',
    })

    const opacity = clsx({
      'cursor-default opacity-50': disabled,
      'opacity-100': !disabled,
    })

    return (
      <button
        {...(onClick && { onClick: onClick })}
        disabled={disabled}
        type={buttonType || 'button'}
        className={`
          ${className ? className : ''}
          flex items-center
          ${justifyContent}
          ring-0
          ${borderRadius}
          ${shadow}
          ${opacity}
          ${padding}
          ${textColor}
          font-semibold
          ${bgColor}
          ${gap}
        `}
        {...(testId && { 'data-testid': testId })}
        {...(tooltipContent && {
          'data-tooltip-id': tooltipId,
          'data-tooltip-content': tooltipContent,
        })}
      >
        {children}
      </button>
    )
  }
)
